/**
 * @file WmsStockPage.tsx
 * @description Реестр остатков и журнал движений (phase13.0; учёт по фасовке —
 * phase19.13 этап 0; общий стиль — phase19.23 A6; фильтры — phase19.24 этап 1,
 * шаг 2).
 *
 * Страница отвечает за три вещи: шапку с переключателем вкладок, справочник
 * складов для фильтров и состояние фильтров в URL. Данные и разметку вкладок
 * держат панели (`StockLevelsPanel`, `StockMovementsPanel`) — так ни один файл
 * экрана не превращается в «страницу на 400 строк».
 *
 * Отдельного селекта склада в шапке нет: фильтры живут одной строкой под ней
 * (чек-лист §10.3), иначе на 360px шапка занимала три ряда.
 *
 * phase19.31 (зона «Склад», минимум по SC50 — экран перестроит phase19.25):
 * заголовок «Остатки» в верхней панели, «Остатки / Движения» — вкладки
 * (`Tabs`, линия снизу), а не пилюли-кнопки; страница без белой карточки.
 */

import { useEffect, useState } from "react";

import { useNotify } from "../../../components/ui/NotifyProvider";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { Tabs, type TabItem } from "../../../components/ui/Tabs";
import { StockMovementsPanel } from "../../../components/wms/StockMovementsPanel";
import { StockLevelsPanel } from "../../../components/wms/stock/StockLevelsPanel";
import { useStockFilters } from "../../../components/wms/stock/use-stock-filters";
import { STOCK_VIEW_LABEL, type StockView } from "../../../components/wms/stock-view";
import { warehousesApi, type WarehouseDto } from "../../../lib/api-wms";

/** Режимы страницы — данными, а не ветками по месту отрисовки. */
const TABS: TabItem<StockView>[] = [
  { value: "stock", label: STOCK_VIEW_LABEL.stock },
  { value: "movements", label: STOCK_VIEW_LABEL.movements },
];

export function WmsStockPage() {
  const notify = useNotify();
  const { filters, setFilters, reset, dirty } = useStockFilters();
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);

  useEffect(() => {
    void warehousesApi
      .list()
      .then(setWarehouses)
      .catch(() => {
        // Молчать нельзя: без справочника фильтр по складу выглядит пустым,
        // как будто складов нет.
        setWarehouses([]);
        notify.error("Не удалось загрузить склады, фильтр недоступен");
      });
  }, [notify]);

  const panelProps = {
    filters,
    onChange: setFilters,
    onReset: reset,
    dirty,
    warehouses,
  };

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader title="Остатки" subtitle="Партии на складах и журнал движений товара" />
      <Tabs
        items={TABS}
        value={filters.view}
        // Поиск и склад переживают смену вкладки (см. VIEW_FIELDS):
        // нашёл товар в остатках — смотришь его же движения.
        onChange={(view) => setFilters({ view })}
        aria-label="Остатки или движения"
      />

      {filters.view === "movements" ? (
        <StockMovementsPanel {...panelProps} />
      ) : (
        <StockLevelsPanel {...panelProps} />
      )}
    </div>
  );
}
